<template>
    <div class="suggestions-page">
        <!-- Hero Section -->
        <div class="suggestions-hero">
            <div class="suggestions-hero_background">
                <div class="suggestions-hero_particles"></div>
                <div class="suggestions-hero_gradient"></div>
            </div>

            <div class="suggestions-hero_content">
                <div class="suggestions-hero_badge">
                    <sw-icon name="magic-wand" />
                    <span>Suas Ideias Importam</span>
                </div>

                <h1 class="suggestions-hero_title">
                    Conte sua ideia e vamos construir juntos
                </h1>

                <p class="suggestions-hero_subtitle">
                    Cada sugestão sua nos inspira a criar uma FileSave ainda melhor.
                    Compartilhe suas ideias, necessidades e sonhos para o futuro da plataforma.
                </p>

                <div class="suggestions-hero_stats">
                    <div class="stat-item">
                        <div class="stat-number">{{ mySuggestions.length }}</div>
                        <div class="stat-label">Suas sugestões enviadas</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-number">{{ implementedCount }}</div>
                        <div class="stat-label">Suas ideias implementadas</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-number">{{ pendingCount }}</div>
                        <div class="stat-label">Aguardando análise</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Navigation Pills -->
        <div class="suggestions-navigation">
            <div class="suggestions-navigation_container">
                <div class="suggestions-pills">
                    <button
                        v-for="tab in navigationTabs"
                        :key="tab.key"
                        class="pill"
                        :class="{ 'pill--active': activeTab === tab.key }"
                        @click="setActiveTab(tab.key)"
                    >
                        <sw-icon :name="tab.icon" />
                        <span>{{ tab.label }}</span>
                        <div class="pill-badge" v-if="tab.count">{{ tab.count }}</div>
                    </button>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="suggestions-content">

            <!-- Nova Sugestão Section -->
            <section v-if="activeTab === 'new'" class="suggestions-section">
                <div class="section-header">
                    <h2>Nova Sugestão</h2>
                    <p>Conte sua ideia de forma detalhada para que possamos entender melhor</p>
                </div>

                <div class="suggestion-form-container">
                    <div class="form-hero">
                        <div class="form-hero_icon">
                            <sw-icon name="magic-wand" />
                        </div>
                        <div class="form-hero_content">
                            <h3>Qual sua ideia?</h3>
                            <p>Seja específico e detalhado. Quanto mais informações, melhor conseguimos entender sua necessidade!</p>
                        </div>
                    </div>

                    <form @submit.prevent="submitSuggestion" class="suggestion-form">
                        <div class="row">
                            <!-- Título da Sugestão -->
                            <div class="grid_col_8 grid_col_m_12 grid_col_s_12 grid_col_xs_12">
                                <sw-input
                                    v-model="newSuggestion.title"
                                    type="text"
                                    label="Título da sua sugestão *"
                                    placeholder="Ex: Integração com Google Drive"
                                    icon="edit"
                                    :validation="titleValidation"
                                    @blur="validateTitle"
                                />
                            </div>

                            <!-- Categoria -->
                            <div class="grid_col_4 grid_col_m_12 grid_col_s_12 grid_col_xs_12 grid_last">
                                <sw-select
                                    v-model="newSuggestion.category"
                                    :options="categoryOptions"
                                    label="Categoria *"
                                    placeholder="Selecione uma categoria"
                                    icon="tag"
                                />
                            </div>

                            <!-- Descrição -->
                            <div class="grid_col_12 grid_col_s_12 grid_col_xs_12">
                                <sw-textarea
                                    v-model="newSuggestion.description"
                                    label="Descrição detalhada *"
                                    icon="file-text"
                                    description="Seja específico e detalhado para nos ajudar a entender sua necessidade"
                                    :validation="descriptionValidation"
                                    @blur="validateDescription"
                                />
                            </div>

                            <!-- Justificativa -->
                            <div class="grid_col_12 grid_col_s_12 grid_col_xs_12">
                                <sw-textarea
                                    v-model="newSuggestion.justification"
                                    label="Por que é importante para você?"
                                    icon="heart"
                                    description="Conte como essa funcionalidade melhoraria seu dia a dia ou resolveria um problema específico que você enfrenta."
                                />
                            </div>

                        </div>

                        <!-- Botões de Ação -->
                        <div class="form-actions">
                            <sw-button
                                name="Enviar Sugestão"
                                icon="paper-plane"
                                color="green"
                                :spin="submittingSuggestion"
                                :disabled="!isFormValid"
                                @click="submitSuggestion"
                            />
                            <sw-button
                                name="Limpar Formulário"
                                icon="refresh"
                                @click="clearForm"
                            />
                        </div>
                    </form>
                </div>
            </section>

            <!-- Minhas Sugestões Section -->
            <section v-if="activeTab === 'mine'" class="suggestions-section">
                <div class="section-header">
                    <h2>Suas Sugestões</h2>
                    <p>Acompanhe o status das suas ideias enviadas</p>
                </div>

                <div class="suggestions-grid" v-if="mySuggestions.length > 0">
                    <article
                        v-for="suggestion in mySuggestions"
                        :key="suggestion.id"
                        class="suggestion-card"
                        :class="`suggestion-card--${suggestion.status}`"
                    >
                        <div class="suggestion-card_header">
                            <div class="suggestion-badge">
                                <sw-icon :name="getStatusIcon(suggestion.status)" />
                                <span>{{ getStatusLabel(suggestion.status) }}</span>
                            </div>
                            <div class="suggestion-date">{{ formatDate(suggestion.date) }}</div>
                        </div>

                        <div class="suggestion-card_content">
                            <h3>{{ suggestion.title }}</h3>
                            <p>{{ suggestion.description }}</p>

                            <div class="suggestion-meta">
                                <span class="suggestion-category">{{ suggestion.category }}</span>
                            </div>
                        </div>

                        <div class="suggestion-card_footer" v-if="suggestion.response">
                            <div class="response-container">
                                <div class="response-header">
                                    <sw-icon name="message-circle" />
                                    <span>Resposta da equipe:</span>
                                </div>
                                <p class="response-text">{{ suggestion.response }}</p>
                            </div>
                        </div>
                    </article>
                </div>

                <div v-else class="empty-suggestions">
                    <div class="empty-icon">
                        <sw-icon name="magic-wand" />
                    </div>
                    <h3>Ainda não há sugestões</h3>
                    <p>Que tal compartilhar sua primeira ideia conosco?</p>
                    <sw-button
                        name="Criar Sugestão"
                        icon="plus"
                        color="green"
                        @click="setActiveTab('new')"
                    />
                </div>
            </section>

        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';
    import { notify } from 'sysweb-vue-components/src/global';

    import { mockMySuggestions } from '@/data/mockData';

    const activeTab = ref('new');
    const submittingSuggestion = ref(false);

    const mySuggestions = ref([]);

    const newSuggestion = ref({
        title: '',
        category: '',
        description: '',
        justification: ''
    });

    const titleValidation = ref('');
    const descriptionValidation = ref('');

    const navigationTabs = [
        { key: 'new', label: 'Nova Sugestão', icon: 'plus', count: null },
        { key: 'mine', label: 'Minhas Sugestões', icon: 'user', count: mySuggestions.value.length }
    ];

    const categoryOptions = [
        { value: 'interface', label: 'Interface e UX' },
        { value: 'features', label: 'Novas Funcionalidades' },
        { value: 'integrations', label: 'Integrações' },
        { value: 'security', label: 'Segurança' },
        { value: 'performance', label: 'Performance' },
        { value: 'mobile', label: 'Mobile' },
        { value: 'api', label: 'API e Automação' },
        { value: 'other', label: 'Outros' }
    ];



    const implementedCount = computed(() => {
        return mySuggestions.value.filter(s => s.status === 'implemented').length;
    });

    const pendingCount = computed(() => {
        return mySuggestions.value.filter(s => s.status === 'pending').length;
    });

    const isFormValid = computed(() => {
        return newSuggestion.value.title.trim() !== '' &&
               newSuggestion.value.category !== '' &&
               newSuggestion.value.description.trim() !== '';
    });

    const setActiveTab = (tab) => {
        activeTab.value = tab;
    };

    const validateTitle = () => {
        if (!newSuggestion.value.title.trim()) {
            titleValidation.value = { isValid: false, error: { message: 'Título é obrigatório' } };
        } else if (newSuggestion.value.title.length < 10) {
            titleValidation.value = { isValid: false, error: { message: 'Título deve ter pelo menos 10 caracteres' } };
        } else {
            titleValidation.value = { isValid: true, error: { message: '' } };
        }
    };

    const validateDescription = () => {
        if (!newSuggestion.value.description.trim()) {
            descriptionValidation.value = { isValid: false, error: { message: 'Descrição é obrigatória' } };
        } else if (newSuggestion.value.description.length < 30) {
            descriptionValidation.value = { isValid: false, error: { message: 'Descrição deve ter pelo menos 30 caracteres' } };
        } else {
            descriptionValidation.value = { isValid: true, error: { message: '' } };
        }
    };

    const submitSuggestion = async () => {
        validateTitle();
        validateDescription();

        if (!titleValidation.value.isValid ||
            !descriptionValidation.value.isValid ||
            !newSuggestion.value.category) {
            notify('Por favor, preencha todos os campos obrigatórios corretamente', 'warning');
            return;
        }

        try {
            submittingSuggestion.value = true;

            // Simular envio
            await new Promise(resolve => setTimeout(resolve, 2000));

            const suggestion = {
                id: Date.now(),
                ...newSuggestion.value,
                date: new Date(),
                status: 'pending',
                response: null
            };

            mySuggestions.value.unshift(suggestion);
            clearForm();

            notify('✨ Sugestão enviada com sucesso! Obrigado por contribuir conosco.', 'success');
            setActiveTab('mine');

        } catch (error) {
            notify('Erro ao enviar sugestão. Tente novamente.', 'error');
        } finally {
            submittingSuggestion.value = false;
        }
    };

    const clearForm = () => {
        newSuggestion.value = {
            title: '',
            category: '',
            description: '',
            justification: ''
        };
        titleValidation.value = { isValid: true, error: { message: '' } };
        descriptionValidation.value = { isValid: true, error: { message: '' } };
    };

    const getStatusIcon = (status) => {
        const icons = {
            pending: 'clock',
            reviewing: 'search-glass',
            approved: 'checkmark',
            implemented: 'star-full',
            rejected: 'close'
        };
        return icons[status] || 'circle';
    };

    const getStatusLabel = (status) => {
        const labels = {
            pending: 'Pendente',
            reviewing: 'Em Análise',
            approved: 'Aprovada',
            implemented: 'Implementada',
            rejected: 'Rejeitada'
        };
        return labels[status] || status;
    };



    const formatDate = (date) => {
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }).format(new Date(date));
    };

    const loadSuggestionsData = () => {
        mySuggestions.value = mockMySuggestions;
    };

    onMounted(() => {
        loadSuggestionsData();
    });
</script>

<style lang="scss" scoped>
@import '@/assets/css/mediaScreen.scss';

.suggestions-page {
    min-height: 100vh;
    background: var(--backgroundDashboard);
}

/* Hero Section */
.suggestions-hero {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: white;
    padding: 30px 0px;

    &_background {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, #10b981 0%, #22c55e 50%, #15803d 100%);
    }

    &_gradient {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(34, 197, 94, 0.8));
    }

    &_particles {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image:
            radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 2px, transparent 2px),
            radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
            radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
        background-size: 120px 120px, 180px 180px, 100px 100px;
        animation: float 25s ease-in-out infinite;
    }

    &_content {
        position: relative;
        z-index: 2;
        text-align: center;
        max-width: 900px;
        padding: 2em;
    }

    &_badge {
        display: inline-flex;
        align-items: center;
        gap: 0.5em;
        padding: 0.6em 1.8em;
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(10px);
        border-radius: 50px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        margin-bottom: 2.5em;
        font-size: 0.95em;
        font-weight: 600;

        .sw-icon {
            font-size: 1.3em;
            animation: bulb-glow 3s ease-in-out infinite;
        }
    }

    &_title {
        font-size: 3.8em;
        font-weight: 800;
        margin-bottom: 0.6em;
        line-height: 1.1;
        background: linear-gradient(135deg, #ffffff, #d1fae5);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;

        @include screen_medias(s, m) {
            font-size: 2.8em;
        }
    }

    &_subtitle {
        font-size: 1.3em;
        margin-bottom: 3.5em;
        opacity: 0.95;
        line-height: 1.6;
        max-width: 700px;
        margin-left: auto;
        margin-right: auto;

        @include screen_medias(s, m) {
            font-size: 1.15em;
            margin-bottom: 2.5em;
        }
    }

    &_stats {
        display: flex;
        justify-content: center;
        gap: 3em;
        margin-top: 2em;

        @include screen_medias(s, m) {
            flex-direction: column;
            gap: 1.5em;
        }

        .stat-item {
            text-align: center;

            .stat-number {
                font-size: 2.8em;
                font-weight: 800;
                margin-bottom: 0.3em;
                line-height: 1;
                background: linear-gradient(135deg, #ffffff, #d1fae5);
                background-clip: text;
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
            }

            .stat-label {
                font-size: 0.95em;
                opacity: 0.9;
                font-weight: 600;
            }
        }
    }
}

@keyframes float {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    25% { transform: translateY(-15px) translateX(8px); }
    50% { transform: translateY(-8px) translateX(-8px); }
    75% { transform: translateY(-20px) translateX(5px); }
}

@keyframes bulb-glow {
    0%, 100% {
        filter: brightness(1);
        transform: scale(1);
    }
    50% {
        filter: brightness(1.3);
        transform: scale(1.1);
    }
}

/* Navigation */
.suggestions-navigation {
    background: white;
    border-bottom: 1px solid var(--borderColorLight);
    position: sticky;
    top: 0;
    z-index: 10;

    &_container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2em;
    }
}

.suggestions-pills {
    display: flex;
    gap: 1em;
    padding: 1.5em 0;
    overflow-x: auto;

    @include screen_medias(s, m) {
        gap: 0.5em;
        padding: 1em 0;
    }
}

.pill {
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.8em 1.6em;
    border: none;
    background: var(--backgroundHover);
    color: var(--textSecondary);
    border-radius: 50px;
    font-weight: 600;
    font-size: 0.9em;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    position: relative;

    &:hover {
        background: var(--colorGreen);
        color: white;
        transform: translateY(-2px);
    }

    &--active {
        background: var(--colorGreen);
        color: white;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

    .pill-badge {
        background: rgba(255, 255, 255, 0.2);
        color: white;
        padding: 0.2em 0.5em;
        border-radius: 12px;
        font-size: 0.8em;
        font-weight: 700;
        min-width: 20px;
        text-align: center;
    }
}

/* Main Content */
.suggestions-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 3em 2em;

    @include screen_medias(s, m) {
        padding: 2em 1em;
    }
}

.suggestions-section {
    margin-bottom: 4em;
}

.section-header {
    text-align: center;
    margin-bottom: 3em;

    h2 {
        font-size: 2.5em;
        font-weight: 800;
        color: var(--textPrimary);
        margin-bottom: 0.5em;
    }

    p {
        font-size: 1.1em;
        color: var(--textSecondary);
    }
}

/* Form Styles */
.suggestion-form-container {
    max-width: 800px;
    margin: 0 auto;
    background: white;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
    border: 1px solid var(--borderColorLight);
}

.form-hero {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(34, 197, 94, 0.05));
    padding: 2.5em 2em;
    display: flex;
    align-items: center;
    gap: 1.5em;
    border-bottom: 1px solid var(--borderColorLight);

    &_icon {
        width: 60px;
        height: 60px;
        border-radius: 16px;
        background: linear-gradient(135deg, #10b981, #22c55e);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 1.8em;
        box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
        flex-shrink: 0;

        .sw-icon {
            animation: bulb-glow 3s ease-in-out infinite;
        }
    }

    &_content {
        h3 {
            font-size: 1.5em;
            font-weight: 700;
            color: var(--textPrimary);
            margin-bottom: 0.5em;
        }

        p {
            color: var(--textSecondary);
            line-height: 1.6;
        }
    }

    @include screen_medias(s, m) {
        flex-direction: column;
        text-align: center;
        padding: 2em 1.5em;
    }
}

.suggestion-form {
    padding: 2.5em 2em;

    @include screen_medias(s, m) {
        padding: 2em 1.5em;
    }
}


.form-actions {
    display: flex;
    gap: 1em;
    justify-content: center;

    @include screen_medias(s, m) {
        flex-direction: column;
    }
}

/* Suggestions Grid */
.suggestions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 2em;

    @include screen_medias(s, m) {
        grid-template-columns: 1fr;
        gap: 1.5em;
    }
}

.suggestion-card {
    background: white;
    border-radius: 16px;
    padding: 2em;
    border: 1px solid var(--borderColorLight);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: var(--colorGreen);
    }

    &--reviewing::before { background: #f59e0b; }
    &--approved::before { background: #3b82f6; }
    &--implemented::before { background: #10b981; }
    &--rejected::before { background: #ef4444; }

    &_header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5em;
    }

    &_content {
        h3 {
            font-size: 1.25em;
            font-weight: 700;
            color: var(--textPrimary);
            margin-bottom: 0.75em;
            line-height: 1.3;
        }

        p {
            color: var(--textSecondary);
            line-height: 1.6;
            margin-bottom: 1.5em;
        }
    }

    &_footer {
        margin-top: 2em;
        padding-top: 1.5em;
        border-top: 1px solid var(--borderColorLight);
    }
}

.suggestion-badge {
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.5em 1em;
    background: var(--backgroundHover);
    border-radius: 20px;
    font-size: 0.8em;
    font-weight: 600;
    color: var(--textSecondary);

    .sw-icon {
        font-size: 1em;
    }
}

.suggestion-date {
    font-size: 0.85em;
    color: var(--textMuted);
}

.suggestion-meta {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1em;

    .suggestion-category {
        padding: 0.3em 0.8em;
        background: var(--backgroundHover);
        border-radius: 12px;
        font-size: 0.75em;
        font-weight: 600;
        color: var(--textSecondary);
    }

}

.response-container {
    .response-header {
        display: flex;
        align-items: center;
        gap: 0.5em;
        margin-bottom: 0.8em;
        color: var(--colorGreen);
        font-weight: 600;
        font-size: 0.9em;

        .sw-icon {
            font-size: 1.1em;
        }
    }

    .response-text {
        color: var(--textSecondary);
        line-height: 1.6;
        font-style: italic;
        padding: 1em;
        background: rgba(16, 185, 129, 0.05);
        border-radius: 12px;
        border-left: 3px solid var(--colorGreen);
        margin: 0;
    }
}


/* Empty State */
.empty-suggestions {
    text-align: center;
    padding: 4em 2em;
    color: var(--textSecondary);

    .empty-icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 2em auto;
        border-radius: 50%;
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(34, 197, 94, 0.1));
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--colorGreen);
        font-size: 2em;
    }

    h3 {
        font-size: 1.5em;
        font-weight: 700;
        color: var(--textPrimary);
        margin-bottom: 0.5em;
    }

    p {
        margin-bottom: 2em;
        font-size: 1.1em;
    }
}
</style>